/** `x-widget: "tag-category-list"` — a multi-select list of tag-category codes.
 *
 * `freeSolo multiple` Autocomplete over the TO's tag categories. The stored
 * value is `string[]` of codes; unknown/not-yet-existing codes are allowed (a
 * deliberately non-existent code is valid). Selected codes render as chips.
 */
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';

import type { TagCategory } from '@/api/integrations';

import { useTagCategories } from './hooks';

interface Props {
  toId: number;
  value: string[];
  onChange: (codes: string[]) => void;
  readOnly?: boolean;
}

/** Map a mixed array of options/strings to a de-duplicated list of codes. */
function toCodes(vals: readonly (string | TagCategory)[]): string[] {
  const out: string[] = [];
  for (const v of vals) {
    const code = (typeof v === 'string' ? v : v.code).trim();
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
}

export function TagCategoryListWidget({ toId, value, onChange, readOnly = false }: Props) {
  const { data: categories, isLoading } = useTagCategories(toId);
  const options = categories ?? [];
  const codes = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

  return (
    <Autocomplete<TagCategory, true, false, true>
      multiple
      freeSolo
      size="small"
      disabled={readOnly}
      loading={isLoading}
      options={options}
      // Values are plain code strings; freeSolo lets us feed strings straight in.
      value={codes}
      onChange={(_, v) => onChange(toCodes(v))}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.code)}
      isOptionEqualToValue={(a, b) => {
        const ac = typeof a === 'string' ? a : a.code;
        const bc = typeof b === 'string' ? b : b.code;
        return ac === bc;
      }}
      renderOption={(props, o) => {
        const cat = o as TagCategory;
        return (
          <Box component="li" {...props} key={cat.id} sx={{ fontSize: 13 }}>
            <Box component="span" sx={{ fontFamily: 'monospace', mr: 1 }}>
              {cat.code}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              {cat.name}
            </Typography>
            {cat.is_inherited && (
              <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                (inherited)
              </Typography>
            )}
          </Box>
        );
      }}
      renderTags={(vals, getTagProps) =>
        vals.map((v, index) => {
          const code = typeof v === 'string' ? v : v.code;
          const { key, ...chipProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={code}
              size="small"
              {...chipProps}
              sx={{ fontFamily: 'monospace' }}
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} placeholder="Tag categories…" />}
    />
  );
}
